import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from 'react-jss';
import { ThemeContext } from 'styled-components';
import { List } from 'immutable';
import { Table, TableCell, TableRow, TableBody } from '@comparethemarketau/manor-table';
import { faExternalLinkAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getBreakpointFromManor } from 'ctm-components';

import { connect } from 'react-redux';
import { coverTypeStateMapping } from '../../products/filters/common/filter_store_mapping';

import { ProductTableRow } from '../common/product_table_row';
import { HtmlCell } from '../cell/html_cell';
import { TableSectionHeader } from '../common/table_section_header';
import { ProductDocuments } from '../../products/products/common/product_documents';
import { UnderWriterCell } from '../cell/underwriter_cell';
import { IconCell } from '../cell/icon_cell';

const useStyles = createUseStyles((theme) => ({
    featureExtraSmallTableDesign: {
        hyphens: 'auto'
    },
    defaultCell: {
        padding: 0
    },
    defaultRow: {
        extend: ({ breakpoint }) => {
            if (breakpoint === 'md' || breakpoint === 'lg') {
                return {
                    borderLeft: `0.2rem solid ${theme.colors.grey300}`,
                    borderRight: `0.2rem solid ${theme.colors.grey300}`
                };
            }
            return {
                borderLeft: `${theme.spacing.px} solid ${theme.colors.grey300}`,
                borderRight: `${theme.spacing.px} solid ${theme.colors.grey300}`
            };
        }
    }
}));

const _ComparingProductsFeaturesBenefits = (props) => {
    const { selectedProducts, cellClassName, columnCount, stackDisplayMode, stagedValue } = props;
    const breakpoint = getBreakpointFromManor();
    const theme = useContext(ThemeContext);
    const classes = useStyles({ theme, columnCount, breakpoint });

    const isCoverTypeNotComprehensive = stagedValue && stagedValue.toUpperCase() !== 'COMPREHENSIVE';

    return (
        <div className={'test-policyComparison-products-featuresBenefits'}>
            <Table className={classes.featureExtraSmallTableDesign} ariaLabel="Products Features and Benefits table" ariaDescribedby="IDREF">
                <TableBody>
                    <TableRow className={classes.defaultRow}>
                        <TableCell className={classes.defaultCell} colspan={columnCount}><TableSectionHeader isKeyInformation={false} /></TableCell>
                    </TableRow>
                    <ProductTableRow
                        key={'Whats_included'}
                        title={'What’s included'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const inclusions = product.getIn(['productMeta', 'inclusions']);
                                const id = product.getIn(['productMeta', 'id']);
                                return (
                                    <HtmlCell htmlText={inclusions} key={`whatsIncluded-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Further_benefits'}
                        title={'Further benefits'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const benefits = product.getIn(['productMeta', 'benefits']);
                                const id = product.getIn(['productMeta', 'id']);
                                return (
                                    <HtmlCell htmlText={benefits} key={`furtherBenefits-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Optional_extras'}
                        title={'Optional extras'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const extra = product.getIn(['productMeta', 'optionalExtras']);
                                const id = product.getIn(['productMeta', 'id']);
                                return (
                                    <HtmlCell htmlText={extra} key={`0ptionalExtras-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Unlimited_KMs'}
                        title={'Unlimited km’s'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const { id, description, value } = findDescriptionAndValueFromFeatures(product, 'annKilo');
                                return (
                                    <IconCell title="Unlimited km’s" description={description} value={value} key={`unlimitedKMs-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Maximum_NCD_protection_option_available'}
                        title={'Maximum NCD protection option available'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const { id, description, value } = findDescriptionAndValueFromFeatures(product, 'ncdPro');
                                return (
                                    <IconCell title="Maximum NCD protection option available" description={description} value={value} key={`maximumNCD-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Accident_hire_car_option_available'}
                        title={'Accident hire car option available'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const { id, description, value } = findDescriptionAndValueFromFeatures(product, 'hirecar');
                                return (
                                    <IconCell title="Accident hire car option available" description={description} value={value} key={`accidentHireCarOption-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Product_Disclosure_Statements'}
                        title={'Product disclosure statements'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const id = product.getIn(['productMeta', 'id']);
                                return (
                                    <ProductDocuments quote={product} key={`productDisclosureStatements-${id}`}>
                                        <div>
                                            PDS
                                            {' '}
                                            <FontAwesomeIcon icon={faExternalLinkAlt} className={classes.icon} />
                                        </div>
                                    </ProductDocuments>
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Personal_Effects_Cover'}
                        title={'Personal effects cover'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        hidden={isCoverTypeNotComprehensive}
                        cells={
                            selectedProducts.map((product) => {
                                const { id, description, value } = findDescriptionAndValueFromFeatures(product, 'personalEf');
                                return (
                                    <IconCell title="Personal effects cover" description={description} value={value} key={`personalEfects-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Child_seat_Baby_capsule'}
                        title={'Child seat/Baby capsule'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        hidden={isCoverTypeNotComprehensive}
                        cells={
                            selectedProducts.map((product) => {
                                const { id, description, value } = findDescriptionAndValueFromFeatures(product, 'childSeat');
                                return (
                                    <IconCell title="Child seat/Baby capsule" description={description} value={value} key={`childSeat-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Instalment_payment_option_available'}
                        title={'Instalment payment option available'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const { id, description, value } = findDescriptionAndValueFromFeatures(product, 'install');
                                return (
                                    <IconCell title="Instalment payment option available" description={description} value={value} key={`install-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Roadside_assistance_option_available'}
                        title={'Roadside assistance option available'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const { id, description, value } = findDescriptionAndValueFromFeatures(product, 'roadSide');
                                return (
                                    <IconCell title="Roadside assistance option available" description={description} value={value} key={`roadSide-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'New_car_replacement'}
                        title={'New car replacement'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const { id, description, value } = findDescriptionAndValueFromFeatures(product, 'newRep');
                                return (
                                    <IconCell title="New car replacement" description={description} value={value} key={`newRep-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Lifetime_guarantee_on_authorised_repairs'}
                        title={'Lifetime guarantee on authorised repairs'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const { id, description, value } = findDescriptionAndValueFromFeatures(product, 'lifRep');
                                return (
                                    <IconCell title="Lifetime guarantee on authorised repairs" description={description} value={value} key={`lifRep-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Carbon_offset_included'}
                        title={'Carbon offset included'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const { id, description, value } = findDescriptionAndValueFromFeatures(product, 'carbOff');
                                return (
                                    <IconCell title="Carbon offset included" description={description} value={value} key={`carbonOffset-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Underwriter'}
                        title={'Underwriter'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const underwriter = product.getIn(['productMeta', 'underwriter']);
                                const id = product.getIn(['productMeta', 'id']);
                                return (
                                    <UnderWriterCell underWriters={underwriter} key={`underwriter-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Disclaimer'}
                        title={'Disclaimer'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const text = product.getIn(['productMeta', 'disclaimer']);
                                const id = product.getIn(['productMeta', 'id']);
                                return (
                                    <HtmlCell htmlText={text} key={`disclaimer-${id}`} />
                                );
                            })
                        }
                    />
                </TableBody>
            </Table>
        </div>
    );
};

_ComparingProductsFeaturesBenefits.propTypes = {
    selectedProducts: PropTypes.instanceOf(List),
    cellClassName: PropTypes.string,
    stackDisplayMode: PropTypes.bool,
    columnCount: PropTypes.number,

    // cover type
    stagedValue: PropTypes.string
};

const ComparingProductsFeaturesBenefits = connect(coverTypeStateMapping)(_ComparingProductsFeaturesBenefits);

const findDescriptionAndValueFromFeatures = (product, code) => {
    if (!product) {
        return {};
    }
    const features = product.getIn(['productMeta', 'features']);
    const id = product.getIn(['productMeta', 'id']);
    const featureObj = features.find((feature) => {
        return feature && feature.get('code') === code;
    });
    return {
        id,
        description: featureObj && featureObj.get('description'),
        value: featureObj && featureObj.get('value')
    };
};

export { ComparingProductsFeaturesBenefits, findDescriptionAndValueFromFeatures };
